/**
 * Training Artifact Quiz — data adapter (Phase Q1).
 *
 * The real adapter calls the SECURITY DEFINER RPCs (get_training_quiz_for_technician
 * strips correct answers; submit_training_quiz_attempt grades server-side). It is
 * inert until those RPCs exist AND the `trainingQuizMvp` flag is on.
 *
 * The mock adapter grades locally against a supplied key — for tests/local dev
 * ONLY (a real client never holds the answer key).
 */
import { supabase } from '@/integrations/supabase/client';
import type { QuizForTechnician, QuizAttemptResult, QuizAnswers } from './quizTypes';
import { scoreQuiz, buildQuestionResults } from './quizGrading';
import type { QuizAuthoringQuestion } from './quizTypes';

export interface SubmitAttemptArgs {
  artifactId: string;
  sessionId: string | null;
  answers: QuizAnswers;
}

export interface QuizAdapter {
  getQuizForTechnician(artifactId: string): Promise<QuizForTechnician | null>;
  submitAttempt(args: SubmitAttemptArgs): Promise<QuizAttemptResult | null>;
}

/** Real adapter — server-side grading via RPCs. Answers never reach the client. */
export function createSupabaseQuizAdapter(): QuizAdapter {
  return {
    async getQuizForTechnician(artifactId) {
      const { data, error } = await supabase.rpc('get_training_quiz_for_technician', {
        _artifact_id: artifactId,
      });
      if (error || !data) return null;
      return data as unknown as QuizForTechnician;
    },
    async submitAttempt({ artifactId, sessionId, answers }) {
      const { data, error } = await supabase.rpc('submit_training_quiz_attempt', {
        _artifact_id: artifactId,
        _session_id: sessionId,
        _answers: answers,
      });
      if (error || !data) return null;
      return data as unknown as QuizAttemptResult;
    },
  };
}

/**
 * Mock adapter — grades locally. TEST/LOCAL ONLY. Never use in production: a real
 * technician client must not hold the answer key.
 */
export function createMockQuizAdapter(
  quiz: { artifactId: string; quizVersion: number; questions: QuizAuthoringQuestion[] },
): QuizAdapter {
  const technicianFacing: QuizForTechnician = {
    artifactId: quiz.artifactId,
    quizVersion: quiz.quizVersion,
    questions: quiz.questions.map((q) => ({ question: q.question, choices: q.choices })),
  };
  return {
    async getQuizForTechnician() {
      return technicianFacing;
    },
    async submitAttempt({ answers }) {
      const key = quiz.questions.map((q) => q.correct_index);
      const s = scoreQuiz(answers, key);
      return {
        attemptId: 'mock-attempt',
        quizVersion: quiz.quizVersion,
        scorePct: s.scorePct,
        passed: s.passed,
        totalQuestions: s.totalQuestions,
        correctCount: s.correctCount,
        questionResults: buildQuestionResults(quiz.questions, answers),
      };
    },
  };
}
